import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { TimelineButtons } from '../components/TimelineButtons';
import { toast } from 'sonner';
import { format, parseISO, subDays, startOfDay } from 'date-fns';
import { 
    CalendarIcon, 
    Plus, 
    X, 
    TrendingUp, 
    Activity,
    ChevronLeft,
    ChevronRight,
    Trash2,
    Save
} from 'lucide-react';
import AttachmentExpander from '../components/AttachmentExpander';
import AttachmentSection from '../components/AttachmentSection';
import useAttachmentCounts from '../hooks/useAttachmentCounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Severity colors
const getSeverityColor = (severity) => {
    if (severity <= 3) return 'bg-emerald-500';
    if (severity <= 5) return 'bg-amber-500';
    if (severity <= 7) return 'bg-orange-500';
    return 'bg-red-500';
};

const getSeverityBgColor = (severity) => {
    if (severity <= 3) return 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800';
    if (severity <= 5) return 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800';
    if (severity <= 7) return 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800';
    return 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
};

const getSeverityText = (severity) => {
    if (severity <= 3) return 'Mild';
    if (severity <= 5) return 'Moderate';
    if (severity <= 7) return 'Significant';
    return 'Severe';
};

export default function SymptomTracker() {
    const { token } = useAuth();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentEntry, setCurrentEntry] = useState(null);
    const [symptoms, setSymptoms] = useState([]);
    const [overallSeverity, setOverallSeverity] = useState(5);
    const [notes, setNotes] = useState('');
    const [commonSymptoms, setCommonSymptoms] = useState([]);
    const [newSymptom, setNewSymptom] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Fetch common symptoms list
    useEffect(() => {
        const fetchCommonSymptoms = async () => {
            try {
                const res = await fetch(`${API_URL}/api/symptoms/common`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setCommonSymptoms(data.symptoms || []);
                }
            } catch (error) {
                console.error('Failed to fetch common symptoms:', error);
            }
        };
        fetchCommonSymptoms();
    }, [token]);

    // Fetch entry for selected date
    const fetchEntryForDate = useCallback(async (date) => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            console.log('[SymptomTracker] Fetching entry for date:', dateStr);
            const res = await fetch(`${API_URL}/api/symptoms/date/${dateStr}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                console.log('[SymptomTracker] Entry received:', data);
                if (data) {
                    setCurrentEntry(data);
                    setSymptoms(data.symptoms || []);
                    setOverallSeverity(data.overall_severity || 5);
                    setNotes(data.notes || '');
                    console.log('[SymptomTracker] Entry loaded with', data.symptoms?.length || 0, 'symptoms');
                } else {
                    console.log('[SymptomTracker] No entry found for date');
                    setCurrentEntry(null);
                    setSymptoms([]);
                    setOverallSeverity(5);
                    setNotes('');
                }
            } else {
                console.error('[SymptomTracker] API error:', res.status);
            }
        } catch (error) {
            console.error('[SymptomTracker] Failed to fetch entry:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Fetch stats and recent entries
    const fetchStats = useCallback(async () => {
        try {
            console.log('[SymptomTracker] Fetching stats and recent entries...');
            const [statsRes, entriesRes] = await Promise.all([
                fetch(`${API_URL}/api/symptoms/stats/summary?days=30`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/symptoms?limit=7`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);
            
            if (statsRes.ok) {
                const statsData = await statsRes.json();
                console.log('[SymptomTracker] Stats received:', statsData);
                setStats(statsData);
            }
            
            if (entriesRes.ok) {
                const entriesData = await entriesRes.json();
                console.log('[SymptomTracker] Recent entries count:', entriesData.length);
                console.log('[SymptomTracker] Entries feeding into correlations:', entriesData.map(e => ({ date: e.date, severity: e.overall_severity })));
                setRecentEntries(entriesData);
            }
        } catch (error) {
            console.error('[SymptomTracker] Failed to fetch stats:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchEntryForDate(selectedDate);
        fetchStats();
    }, [selectedDate, fetchEntryForDate, fetchStats]);

    // Add symptom
    const addSymptom = (name) => {
        if (!name.trim()) return;
        if (symptoms.find(s => s.name.toLowerCase() === name.toLowerCase())) {
            toast.error('Symptom already added');
            return;
        }
        setSymptoms([...symptoms, { name: name.trim(), severity: 5, notes: '' }]);
        setNewSymptom('');
    };

    // Update symptom
    const updateSymptom = (index, field, value) => {
        const updated = [...symptoms];
        updated[index] = { ...updated[index], [field]: value };
        setSymptoms(updated);
    };

    // Remove symptom
    const removeSymptom = (index) => {
        setSymptoms(symptoms.filter((_, i) => i !== index));
    };

    // Attachment counts for recent entries list
    const recentEntryIds = recentEntries.map(e => e.id);
    const attachmentCounts = useAttachmentCounts('symptom', recentEntryIds);

    // Save entry
    const saveEntry = async () => {
        if (symptoms.length === 0) {
            toast.error('Please add at least one symptom');
            return;
        }

        setSaving(true);
        try {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const res = await fetch(`${API_URL}/api/symptoms`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    date: dateStr,
                    symptoms,
                    overall_severity: overallSeverity,
                    notes: notes || null
                })
            });

            if (res.ok) {
                const data = await res.json();
                setCurrentEntry(data);
                toast.success('Symptoms saved successfully');
                fetchStats();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save symptoms');
        } finally {
            setSaving(false);
        }
    };

    // Delete entry
    const deleteEntry = async () => {
        if (!currentEntry) return;
        
        if (!window.confirm('Are you sure you want to delete this entry?')) return;

        try {
            const res = await fetch(`${API_URL}/api/symptoms/${currentEntry.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                setCurrentEntry(null);
                setSymptoms([]);
                setOverallSeverity(5);
                setNotes('');
                toast.success('Entry deleted');
                fetchStats();
            }
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    // Navigate dates
    const goToPreviousDay = () => {
        setSelectedDate(prev => subDays(prev, 1));
    };

    const goToNextDay = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (selectedDate < startOfDay(tomorrow)) {
            setSelectedDate(prev => {
                const next = new Date(prev);
                next.setDate(next.getDate() + 1);
                return next;
            });
        }
    };

    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Symptom Tracker</h1>
                    <p className="text-muted-foreground mt-1">Track your daily symptoms and identify patterns</p>
                </div>
                <TimelineButtons />
            </div>

            {/* Stats Cards */}
            {stats && stats.total_entries > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/20 border-indigo-200 dark:border-indigo-800">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-indigo-500 rounded-lg">
                                    <Activity className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Entries (30d)</p>
                                    <p className="text-xl font-bold">{stats.total_entries}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={`border ${stats.average_severity ? getSeverityBgColor(stats.average_severity) : ''}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${stats.average_severity ? getSeverityColor(stats.average_severity) : 'bg-gray-400'}`}>
                                    <TrendingUp className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Avg Severity</p>
                                    <p className="text-xl font-bold">{stats.average_severity || '-'}/10</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {stats.most_common_symptoms?.slice(0, 2).map((s, idx) => (
                        <Card key={idx} className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/30">
                            <CardContent className="p-4">
                                <p className="text-xs text-muted-foreground">Top Symptom</p>
                                <p className="font-semibold truncate">{s.name}</p>
                                <p className="text-xs text-muted-foreground">{s.count} occurrences</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Main Entry Form */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Date Navigation */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={goToPreviousDay}
                                    data-testid="prev-day-btn"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </Button>
                                
                                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <Button 
                                            variant="outline" 
                                            className="min-w-[200px]"
                                            data-testid="date-picker-btn"
                                        >
                                            <CalendarIcon className="w-4 h-4 mr-2" />
                                            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="center">
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedDate(date);
                                                    setCalendarOpen(false);
                                                }
                                            }}
                                            disabled={(date) => date > new Date()}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={goToNextDay}
                                    disabled={isToday}
                                    data-testid="next-day-btn"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </Button>
                            </div>
                            {isToday && (
                                <p className="text-center text-sm text-muted-foreground mt-2">Today</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Symptom Entry */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Log Symptoms</CardTitle>
                            <CardDescription>
                                {currentEntry ? 'Update your symptoms for this day' : 'Record your symptoms for this day'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : (
                                <>
                                    {/* Quick Add Symptoms */}
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Quick Add Common Symptoms</label>
                                        <div className="flex flex-wrap gap-2">
                                            {commonSymptoms.slice(0, 12).map((symptom) => (
                                                <Badge
                                                    key={symptom}
                                                    variant={symptoms.find(s => s.name === symptom) ? "default" : "outline"}
                                                    className="cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors"
                                                    onClick={() => addSymptom(symptom)}
                                                    data-testid={`quick-add-${symptom.toLowerCase().replace(/\s+/g, '-')}`}
                                                >
                                                    {symptom}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Custom Symptom Input */}
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Add Custom Symptom</label>
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder="Enter symptom name..."
                                                value={newSymptom}
                                                onChange={(e) => setNewSymptom(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && addSymptom(newSymptom)}
                                                data-testid="custom-symptom-input"
                                            />
                                            <Button 
                                                onClick={() => addSymptom(newSymptom)}
                                                disabled={!newSymptom.trim()}
                                                data-testid="add-symptom-btn"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Active Symptoms List */}
                                    {symptoms.length > 0 && (
                                        <div>
                                            <label className="text-sm font-medium mb-3 block">Today's Symptoms ({symptoms.length})</label>
                                            <div className="space-y-3">
                                                {symptoms.map((symptom, index) => (
                                                    <div 
                                                        key={index}
                                                        className={`p-4 rounded-xl border ${getSeverityBgColor(symptom.severity)}`}
                                                        data-testid={`symptom-entry-${index}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="font-medium">{symptom.name}</span>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-100"
                                                                onClick={() => removeSymptom(index)}
                                                                data-testid={`remove-symptom-${index}`}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs text-muted-foreground w-16">Severity:</span>
                                                                <input
                                                                    type="range"
                                                                    min="1"
                                                                    max="10"
                                                                    value={symptom.severity}
                                                                    onChange={(e) => updateSymptom(index, 'severity', parseInt(e.target.value))}
                                                                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                                    data-testid={`severity-slider-${index}`}
                                                                />
                                                                <Badge className={getSeverityColor(symptom.severity)}>
                                                                    {symptom.severity}/10
                                                                </Badge>
                                                            </div>
                                                            <Input
                                                                placeholder="Optional notes for this symptom..."
                                                                value={symptom.notes || ''}
                                                                onChange={(e) => updateSymptom(index, 'notes', e.target.value)}
                                                                className="text-sm"
                                                                data-testid={`symptom-notes-${index}`}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Overall Severity */}
                                    <div className={`p-4 rounded-xl border ${getSeverityBgColor(overallSeverity)}`}>
                                        <label className="text-sm font-medium mb-3 block">Overall Day Severity</label>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={overallSeverity}
                                                onChange={(e) => setOverallSeverity(parseInt(e.target.value))}
                                                className="flex-1 h-3 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                data-testid="overall-severity-slider"
                                            />
                                            <div className="text-center min-w-[80px]">
                                                <Badge className={`${getSeverityColor(overallSeverity)} text-lg px-3 py-1`}>
                                                    {overallSeverity}/10
                                                </Badge>
                                                <p className="text-xs mt-1">{getSeverityText(overallSeverity)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Daily Notes</label>
                                        <Textarea
                                            placeholder="Any additional notes about how you're feeling today, potential triggers, treatments tried, etc..."
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            rows={3}
                                            data-testid="daily-notes-input"
                                        />
                                    </div>

                                    {/* Attachments — only after entry is saved */}
                                    {currentEntry?.id && (
                                        <AttachmentSection
                                            parentType="symptom"
                                            parentId={currentEntry.id}
                                            enabled={true}
                                        />
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-3 pt-2">
                                        <Button 
                                            onClick={saveEntry} 
                                            disabled={saving || symptoms.length === 0}
                                            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                            data-testid="save-symptoms-btn"
                                        >
                                            <Save className="w-4 h-4 mr-2" />
                                            {saving ? 'Saving...' : currentEntry ? 'Update Entry' : 'Save Entry'}
                                        </Button>
                                        {currentEntry && (
                                            <Button 
                                                variant="destructive" 
                                                onClick={deleteEntry}
                                                data-testid="delete-entry-btn"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Recent Entries */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Recent Entries</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {recentEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    No entries yet. Start tracking your symptoms!
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {recentEntries.map((entry) => (
                                        <div
                                            key={entry.id}
                                            className={`p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${getSeverityBgColor(entry.overall_severity)}`}
                                            onClick={() => setSelectedDate(parseISO(entry.date))}
                                            data-testid={`recent-entry-${entry.date}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium">
                                                    {format(parseISO(entry.date), 'MMM d, yyyy')}
                                                </span>
                                                <Badge className={getSeverityColor(entry.overall_severity)}>
                                                    {entry.overall_severity}/10
                                                </Badge>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {entry.symptoms.slice(0, 3).map((s, i) => (
                                                    <span key={i} className="text-xs text-muted-foreground">
                                                        {s.name}{i < Math.min(entry.symptoms.length - 1, 2) ? ',' : ''}
                                                    </span>
                                                ))}
                                                {entry.symptoms.length > 3 && (
                                                    <span className="text-xs text-muted-foreground">
                                                        +{entry.symptoms.length - 3} more
                                                    </span>
                                                )}
                                                {(attachmentCounts[`symptom:${entry.id}`] || 0) > 0 && (
                                                    <span className="text-xs text-indigo-600 dark:text-indigo-400 ml-auto" data-testid={`symptom-paperclip-${entry.id}`}>
                                                        📎 {attachmentCounts[`symptom:${entry.id}`]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Most Common Symptoms */}
                    {stats && stats.most_common_symptoms?.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Top Symptoms (30 days)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {stats.most_common_symptoms.map((s, idx) => (
                                        <div key={idx} className="flex items-center justify-between">
                                            <span className="text-sm">{s.name}</span>
                                            <Badge variant="secondary">{s.count}x</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Tips Card */}
                    <Card className="bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/30 dark:to-indigo-950/20 border-sky-200 dark:border-sky-800">
                        <CardHeader>
                            <CardTitle className="text-lg">💡 Tracking Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Track symptoms at the same time daily for consistency</p>
                            <p>• Note potential triggers like food, stress, or weather</p>
                            <p>• Be specific about symptom locations</p>
                            <p>• Include any treatments or remedies tried</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
